import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { designTokens } from '../../theme/designTokens';
import predefinedPrompts from '../../data/predefinedPrompts.json';
import {
  selectSelectedPrompt,
  setSelectedPrompt
} from '../../features/analysis/analysisSlice';

const PredefinedPrompts = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const selectedPrompt = useSelector(selectSelectedPrompt);
  const [localSelectedPrompt, setLocalSelectedPrompt] = useState(null);

  useEffect(() => {
    // Auto-select the first prompt if none is selected
    if (!selectedPrompt && predefinedPrompts.length > 0) {
      const firstPrompt = predefinedPrompts[0];
      dispatch(setSelectedPrompt(firstPrompt));
      setLocalSelectedPrompt(firstPrompt);
    } else {
      setLocalSelectedPrompt(selectedPrompt);
    }
  }, [selectedPrompt, dispatch]);

  const handlePromptSelect = (prompt) => {
    setLocalSelectedPrompt(prompt);
    dispatch(setSelectedPrompt(prompt));
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Prompts List Header */}
      <Box sx={{
        p: designTokens.spacing.xxs,
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        backgroundColor: theme.palette.background.paper
      }}>
        <Typography variant="subtitle1" sx={{
          fontWeight: designTokens.fontWeight.semibold,
          color: theme.palette.text.primary
        }}>
          Predefined Prompts
        </Typography>
        <Chip
          label={`${predefinedPrompts.length} available`}
          size="small"
          sx={{
            mt: 0.5,
            fontSize: designTokens.fontSize.xs,
            backgroundColor: theme.palette.primary.main + '20',
            color: theme.palette.primary.dark
          }}
        />
      </Box>

      {/* Prompts List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List sx={{ p: 0 }}>
          {predefinedPrompts.map((prompt) => (
            <ListItem key={prompt.id} disablePadding>
              <ListItemButton
                selected={localSelectedPrompt?.id === prompt.id}
                onClick={() => handlePromptSelect(prompt)}
                sx={{
                  p: designTokens.spacing.xxs,
                  '&.Mui-selected': {
                    backgroundColor: theme.palette.primary.main + '15',
                    borderRight: `3px solid ${theme.palette.primary.main}`,
                  },
                  '&.Mui-selected:hover': {
                    backgroundColor: theme.palette.primary.main + '20',
                  },
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  }
                }}
              >
                <ListItemText
                  primary={prompt.name}
                  primaryTypographyProps={{
                    sx: {
                      fontWeight: localSelectedPrompt?.id === prompt.id
                        ? designTokens.fontWeight.semibold
                        : designTokens.fontWeight.medium,
                      fontSize: designTokens.fontSize.sm,
                      color: localSelectedPrompt?.id === prompt.id
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                      lineHeight: designTokens.lineHeight.tight
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* Description Section */}
      {localSelectedPrompt && (
        <Paper
          elevation={0}
          sx={{
            p: designTokens.spacing.xs,
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: designTokens.borderRadius.xs
          }}
        >
          <Typography variant="subtitle2" sx={{
            fontWeight: designTokens.fontWeight.semibold,
            mb: designTokens.spacing.sm,
            color: theme.palette.text.primary
          }}>
            Description
          </Typography>
          <Typography variant="body2" sx={{
            fontSize: designTokens.fontSize.sm,
            lineHeight: designTokens.lineHeight.normal,
            color: theme.palette.text.secondary
          }}>
            {localSelectedPrompt.description}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default PredefinedPrompts;
